/**
 * The streaming transport (ADR-103): `POST {streamUrl}/chat/message` answers
 * `application/x-ndjson`, one event per line —
 *
 *   {"type":"session","sessionId":"…"}   first: the id to carry on with
 *   {"type":"delta","text":"…"}           visible text, safe to append
 *   {"type":"done", …reply body…}         authoritative: what the reply IS
 *   {"type":"error","status":403, …body…} a turn that ended without an answer
 *
 * `done` carries the REST route's whole response body (`reply`, `sessionId`,
 * `suggestions`, `suggest_lead_capture`, `cartId`, or a canned `code`), and
 * the widget REPLACES what it streamed with `done.reply`: the server checks
 * the whole reply after the stream — a link it drops, a stray comment it
 * strips, a leak it refuses — and the stream is a preview of that, never a
 * substitute for it.
 *
 * The one fallback is deliberate and narrow: a fetch that fails BEFORE any
 * response headers (the URL unreachable, the function absent, a non-stream
 * answer from something that is not our server) is `unreachable`, and the
 * caller sends the same message over the REST route, once, with a warning
 * in the console. Nothing after the first byte falls back — a turn that
 * reached the backend was admitted and metered, and a second send would be
 * a second spend. A stream that breaks after that is `broken`: a failed turn
 * with a retry, like a REST failure.
 */

export type StreamEvent =
  | { type: 'session'; sessionId: string }
  | { type: 'delta'; text: string }
  | { type: 'done'; body: Record<string, unknown> }
  | { type: 'error'; status: number; body: Record<string, unknown> };

/** One JSON object per line, whatever the chunk boundaries did to the lines. */
export class NdjsonLines {
  private buffer = '';

  /** Feed a chunk; get back every complete line it closed, parsed. */
  push(chunk: string): unknown[] {
    this.buffer += chunk;
    const out: unknown[] = [];
    let at = this.buffer.indexOf('\n');
    while (at !== -1) {
      const line = this.buffer.slice(0, at).trim();
      this.buffer = this.buffer.slice(at + 1);
      if (line) out.push(parseLine(line));
      at = this.buffer.indexOf('\n');
    }
    return out;
  }

  /** The last line, if the stream ended without a newline. */
  flush(): unknown[] {
    const line = this.buffer.trim();
    this.buffer = '';
    return line ? [parseLine(line)] : [];
  }
}

function parseLine(line: string): unknown {
  try {
    return JSON.parse(line);
  } catch {
    return null;
  }
}

/** Shape check for one parsed line; null for anything that is not one of ours. */
export function parseStreamEvent(raw: unknown): StreamEvent | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  switch (o.type) {
    case 'session':
      return typeof o.sessionId === 'string' && o.sessionId ? { type: 'session', sessionId: o.sessionId } : null;
    case 'delta':
      return typeof o.text === 'string' ? { type: 'delta', text: o.text } : null;
    case 'done': {
      const { type: _t, ...body } = o;
      return { type: 'done', body };
    }
    case 'error': {
      const { type: _t, status, ...body } = o;
      return { type: 'error', status: typeof status === 'number' ? status : 200, body };
    }
    default:
      return null;
  }
}

export type StreamOutcome =
  | { kind: 'done'; body: Record<string, unknown> }
  | { kind: 'error'; status: number; body: Record<string, unknown> }
  /** Nothing reached our server: no headers, or an answer that is not a stream. Safe to resend over REST. */
  | { kind: 'unreachable'; reason: string }
  /** The stream started and ended without `done` or `error`. NOT safe to resend. */
  | { kind: 'broken' };

export interface StreamHandlers {
  onSession?: (sessionId: string) => void;
  onDelta: (text: string) => void;
}

export async function streamMessage(
  url: string,
  payload: unknown,
  handlers: StreamHandlers,
  signal: AbortSignal,
): Promise<StreamOutcome> {
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal,
      body: JSON.stringify(payload),
    });
  } catch (err) {
    // An abort is the caller's timeout: the request may well have arrived,
    // so it is a broken turn, not an unreachable host.
    if (signal.aborted) return { kind: 'broken' };
    return { kind: 'unreachable', reason: err instanceof Error ? err.message : 'network error' };
  }

  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('x-ndjson')) {
    // Not our server's answer: a dead URL's error page, a gateway's 404, a
    // proxy. Whatever it was, it did not run a turn.
    return { kind: 'unreachable', reason: `HTTP ${res.status} (${contentType || 'no content type'})` };
  }
  if (!res.body) return { kind: 'broken' };

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  const lines = new NdjsonLines();

  const handle = (raw: unknown): StreamOutcome | null => {
    const event = parseStreamEvent(raw);
    if (!event) return null;
    if (event.type === 'session') {
      handlers.onSession?.(event.sessionId);
      return null;
    }
    if (event.type === 'delta') {
      if (event.text) handlers.onDelta(event.text);
      return null;
    }
    if (event.type === 'done') return { kind: 'done', body: event.body };
    return { kind: 'error', status: event.status, body: event.body };
  };

  try {
    for (;;) {
      const { value, done } = await reader.read();
      const chunk = value ? decoder.decode(value, { stream: !done }) : '';
      const parsed = chunk ? lines.push(chunk) : [];
      if (done) parsed.push(...lines.flush());
      for (const raw of parsed) {
        const outcome = handle(raw);
        if (outcome) {
          reader.cancel().catch(() => undefined);
          return outcome;
        }
      }
      if (done) return { kind: 'broken' };
    }
  } catch {
    return { kind: 'broken' };
  }
}
